import {
  type DeviceTrip,
  DeviceTripsRequester,
  GraphQLRoutingRequester,
  isWebDataError,
  isWebDataNotAsked,
  isWebDataSuccess,
  type NewRoute,
  type NewRouteReport,
  NewRoutingReportType,
  ProofOfConclusionRequester,
  type ProofsOfConclusion,
  SpreadsheetReportTemplateRequester,
  type WebData,
} from '@cobliteam/api-lib';
import type { DateRange, UUID, Vehicle } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import { RoutingSpreadsheetNoPermissionMessage } from '@cobliteam/routing/src/permissions';
import { loadVehiclesRequest, selectVehicles } from '@cobliteam/store/src/Vehicles';
import Alert from '@cobliteam/ui/components/Alert';
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { ActivitySpreadsheetReportBuilder } from '../../reports/ActivitySpreadsheetReport';
import { RouteExportBuilder } from '../../reports/RouteExportBuilder';
import { RoutesSpreadsheetReportBuilder } from '../../reports/RoutesSpreadsheetReport';
import { parserRouteEditingErrors } from '../Routing/errors';
import {
  exportRoutesFailed,
  exportRoutesReportFailed,
  exportRoutesReportSucceeded,
  exportRoutesSucceeded,
  routeExecutionInfoFailed,
  routeExecutionInfoSucceeded,
  routesDeleteFailed,
  routesDeleteSucceeded,
  routeUpdateFailed,
  routeUpdateSucceeded,
} from './actionCreators';
import type { ExportNewRoutingReportRequest, ExportRoutesRequest, RouteExecutionInfoRequest, RoutesDeleteRequest, RouteUpdateRequest } from './actions';
import { ActionTypes } from './actionTypes';
import { downloadReportFile, downloadRouteSpreadSheetFile } from './utils';

const env = Environment.getInstance().getEnvironment();
export const deviceTripsRequester = new DeviceTripsRequester({
  host: env.herbieApiHost,
});
export const spreadsheetReportTemplateRequester = new SpreadsheetReportTemplateRequester();
export const graphQLRoutingRequester: GraphQLRoutingRequester = new GraphQLRoutingRequester();
export const routesSpreadsheetReportBuilder = new RoutesSpreadsheetReportBuilder();
export const routeExportBuilder = new RouteExportBuilder();
export const activitySpreadsheetReportBuilder = new ActivitySpreadsheetReportBuilder();
export const proofOfConclusionRequester = new ProofOfConclusionRequester({
  host: env.herbieApiHost,
});

export const defaultAlertConfig = {
  closeCallback: () => {},
};

export function* requestRouteUpdateSaga(action: RouteUpdateRequest) {
  try {
    const updatedRoute: NewRoute = yield call(graphQLRoutingRequester.editRoute, action.payload.route, action.payload.hasPermission);

    yield put(routeUpdateSucceeded(updatedRoute));
  } catch (err) {
    yield put(routeUpdateFailed(parserRouteEditingErrors(err)));
  }
}

export function* requestRouteExecutionInfoSaga(action: RouteExecutionInfoRequest) {
  const { deviceId, startTime, endTime, mapMatch } = action.payload;

  try {
    const routeInfo: DeviceTrip = yield call(deviceTripsRequester.getDeviceTrips, deviceId, startTime, endTime, mapMatch);
    yield put(routeExecutionInfoSucceeded(routeInfo));
  } catch (err) {
    yield put(routeExecutionInfoFailed());
  }
}

export function* requestRoutesDeleteSaga(action: RoutesDeleteRequest) {
  try {
    const { routesToDelete, notifyDeleteRouteRequestResult } = action.payload;
    const deletedRoutes: UUID[] = yield call(graphQLRoutingRequester.deleteRoutes, routesToDelete);
    yield put(routesDeleteSucceeded(deletedRoutes));
    if (notifyDeleteRouteRequestResult) {
      Alert.success('Rota excluída com sucesso!');
    }
  } catch (err) {
    yield put(routesDeleteFailed());
    Alert.error('Ocorreu um erro ao excluir a rota. Tente novamente');
  }
}

export function* requestExportRoutesSaga(action: ExportRoutesRequest) {
  Alert.loading('Exportando rota...', { id: 'exporting-route' });

  try {
    const { route } = action.payload;
    let vehiclesWebData: WebData<Vehicle[]> = yield select(selectVehicles);

    if (isWebDataNotAsked(vehiclesWebData) || isWebDataError(vehiclesWebData)) {
      yield put(loadVehiclesRequest());
      vehiclesWebData = yield select(selectVehicles);
    }

    if (isWebDataSuccess(vehiclesWebData)) {
      const spreadsheetTemplate: Blob = yield call(spreadsheetReportTemplateRequester.getRouteExportTemplate);
      const file: Blob = yield call(routeExportBuilder.buildReport, spreadsheetTemplate, route, vehiclesWebData.data);
      yield call(downloadRouteSpreadSheetFile, file, route);
    }

    yield put(exportRoutesSucceeded());
    Alert.dismiss('exporting-route');
    Alert.success('Rota exportada com sucesso');
  } catch (err) {
    Alert.dismiss('exporting-route');
    Alert.error('Houve um erro, tente novamente mais tarde.');
    yield put(exportRoutesFailed());
  }
}

export function* exportRoutesReport(routesReport: NewRouteReport[], dateRange: DateRange) {
  const spreadsheetTemplate: Blob = yield call(spreadsheetReportTemplateRequester.getRoutesReportTemplate);
  const report: Blob = yield call(routesSpreadsheetReportBuilder.buildReport, spreadsheetTemplate, dateRange, routesReport);

  yield call(downloadReportFile, report, 'relatorio_de_rotas', dateRange.start, dateRange.end);
}

export function* exportActivitiesReport(routesReport: NewRouteReport[], dateRange: DateRange) {
  const spreadsheetTemplate: Blob = yield call(spreadsheetReportTemplateRequester.getActivitiesTemplate);

  const report: Blob = yield call(activitySpreadsheetReportBuilder.buildReport, spreadsheetTemplate, dateRange, routesReport);

  yield call(downloadReportFile, report, 'relatorio_de_atividades', dateRange.start, dateRange.end);
}

export function* requestExportRoutesReportSaga(action: ExportNewRoutingReportRequest) {
  try {
    const { reportType, dateRange, vehicleGroupIds, hasPermission } = action.payload;

    const routesReport: NewRouteReport[] = yield call(graphQLRoutingRequester.requestRoutesReport, dateRange);

    const filteredRoutesReport =
      vehicleGroupIds?.length != 0
        ? routesReport.filter((route) => {
            const groupId = route.vehicle?.groups?.[0]?.id;
            return groupId && vehicleGroupIds?.includes(groupId);
          })
        : routesReport;

    const earliestStartTime = filteredRoutesReport.reduce(
      (earliest, report) => Math.min(report?.startTime?.planned || dateRange.start, report?.startTime?.executed || dateRange.start, earliest),
      dateRange.start
    );

    const latestEndTime = filteredRoutesReport.reduce(
      (latest, report) => Math.max(report?.endTime?.planned || dateRange.end, report?.endTime?.executed || dateRange.end, latest),
      dateRange.end
    );

    const proofsOfConclusion: ProofsOfConclusion | undefined = hasPermission ? yield call(proofOfConclusionRequester.load, 10000, 0, earliestStartTime, latestEndTime) : undefined;

    const routesReportAuthorized: NewRouteReport[] = filteredRoutesReport.map((report) => {
      const updatedActivities = report.activities.map((activity) => {
        if (!hasPermission) {
          return {
            ...activity,
            observations: RoutingSpreadsheetNoPermissionMessage.POC,
          };
        }

        const proofOfConclusion = proofsOfConclusion?.proofsOfConclusion.find((element) => element.activityId === activity.id);
        if (proofOfConclusion) {
          return {
            ...activity,
            observations: proofOfConclusion.observation,
          };
        }

        return activity;
      });

      return {
        ...report,
        activities: updatedActivities,
      };
    });

    yield reportType === NewRoutingReportType.ROUTES
      ? call(exportRoutesReport, routesReportAuthorized, dateRange)
      : call(exportActivitiesReport, routesReportAuthorized, dateRange);
    yield put(exportRoutesReportSucceeded());
    Alert.success('Relatório exportado com sucesso!');
  } catch (err) {
    yield put(exportRoutesReportFailed());
    Alert.error('Ocorreu um erro ao gerar o relatório, tente novamente.');
  }
}

export function* RoutesSagasListener() {
  yield all([
    takeEvery(ActionTypes.ROUTE_UPDATE_REQUEST, requestRouteUpdateSaga),
    takeLatest(ActionTypes.ROUTE_EXECUTION_INFO_REQUEST, requestRouteExecutionInfoSaga),
    takeLatest(ActionTypes.ROUTES_DELETE_REQUEST, requestRoutesDeleteSaga),
    takeLatest(ActionTypes.EXPORT_ROUTES_REQUEST, requestExportRoutesSaga),
    takeLatest(ActionTypes.EXPORT_NEW_ROUTING_REPORT_REQUEST, requestExportRoutesReportSaga),
  ]);
}
